/**
 * We want to handle the polling and debouncing of requests:
 *  - be able to repeatedly poll an endpoint at intervals
 *  - be able to kill poll given a certain returned value
 *  - kill a poll after certain amount of time or requests
 *  - prevent making similar requests in progress
 *  - abstract the request-making and response-handling process
 *
 * a poll has:
 *  - Interval (min)
 *  - fetch
 *  - debouncer
 *  - streams (next, error, complete)
 *  - kill
 *  - max attempts
 *  - polling/not polling state
 *  - requesting/not requesting state
 *  - some kind of ID
 *
 * a debouncer has:
 *  - some kind of ID
 *  - reference to the request to be debounced
 *  - min interval between requests
 *  - request in progress state
 *  - request previous time state
 *
 * a request has:
 *  - the url (incl. queries)
 *  - req options
 *  - check response function
 *  - and in most cases, the response is parsed with .json()
 */

import { Reducer } from "redux"
import { NetworkType, AuthUserType } from "../../Actions/Types"
import { AuthAction } from "../Auth"

interface DebounceState {
    inProgress: boolean
    previousTime: number
    interval: number
}

interface PollState {
    startTimestamp?: number
}

export interface NetworkState {
    debounce: {
        [id: string]: DebounceState | undefined
    }
    poll: {
        [id: string]: PollState | undefined
    }
}

interface INetworkAction {
    type: NetworkType | null
    payload?: {
        id: string
        debounce?: DebounceState
        poll?: PollState
    }
}

export type NetworkAction = INetworkAction

const initialState: NetworkState = {
    debounce: {},
    poll: {}
}

export const NetworkReducer: Reducer<NetworkState, NetworkAction | AuthAction> =
    (state = initialState, action): NetworkState => {
        switch (action.type) {
            case NetworkType.setDebounceState:
                return action.payload && action.payload.id
                    ? {
                          ...state,
                          debounce: {
                              ...state.debounce,
                              [action.payload.id]: action.payload.debounce
                          }
                      }
                    : state
            case NetworkType.setPollState:
                return action.payload && action.payload.id
                    ? {
                          ...state,
                          poll: {
                              ...state.poll,
                              [action.payload.id]: action.payload.poll
                          }
                      }
                    : state
            case AuthUserType.signedOut:
                return initialState
            default:
                return state
        }
    }
